import {
    Component,
    Injector,
    ViewChild,
    ViewEncapsulation,
    OnInit,
    OnDestroy,
} from '@angular/core';
import { appModuleAnimation } from '@axilla/axilla-shared';
import { AppComponentBase } from '@shared/common/app-component-base';
import {
    CustomerDto,
    VirtualBankAccountDto,
    ICreateVirtualBankUserInputDtoWithId,
    IConfirmWebshopOrderInputDto,
    WebshopSendBasketOutput,
    OrderScheduleServiceProxy,
    ChangeCommunicationEmailDto,
    GetCommunicationEmailOutputDto,
    CreateOrUpdateVirtualBankDto,
    ICreateOrUpdateVirtualBankDto
} from '@shared/service-proxies/service-proxies';
import { Paginator } from 'primeng/paginator';
import { Table } from 'primeng/table';

import {catchError, debounceTime, finalize, single, take, takeUntil} from 'rxjs/operators';
import {
    FieldAgentService,
} from '@app/webshop/field-agent/shared/services/field-agent.service';
import {Subject} from '@node_modules/rxjs';
import {
    ViewAllManagersModalComponent
} from '@app/webshop/field-agent/modals/managers/view-all-managers-modal.component';
import {AddManagerModalComponent} from '@app/webshop/field-agent/modals/managers/add-manager-modal.component';
import {EditCustomerModalComponent} from '@app/webshop/field-agent/modals/customer/edit-customer-modal.component';
import {NgxSpinnerService} from '@node_modules/ngx-spinner';
import {WebshopService} from '@app/shared/common/shop-system/services/webshop.service';
import {EditCustomerMode} from '@app/webshop/shared/models';
import {
    ChangeNotificationEmailModalComponent
} from '@app/webshop/field-agent/modals/order-confirmation/change-notification-email-modal.component';
import {FieldAgentOrderService} from '@app/webshop/field-agent/shared/services/field-agent-order.service';
import moment from 'moment';
import {Router} from '@angular/router';
import _ from 'lodash';
import {SignaturePadComponent} from '@app/webshop/field-agent/shared/common/signature/signature-pad.component';

@Component({
    templateUrl: './field-agent-order-confirmation.component.html',
    styleUrls: ['./field-agent-order-confirmation.component.less'],
    encapsulation: ViewEncapsulation.None,
    animations: [appModuleAnimation()]
})
export class FieldAgentOrderConfirmationComponent extends AppComponentBase implements OnInit, OnDestroy {
    spinnerService: NgxSpinnerService;

    public readonly EditCustomerMode = EditCustomerMode;

    @ViewChild('changeNotificationEmailModal', {static: true}) changeNotificationEmailModal: ChangeNotificationEmailModalComponent;
    @ViewChild('viewAllManagersModal', {static: true}) viewAllManagersModal: ViewAllManagersModalComponent;
    @ViewChild('addManagerModal', {static: true}) addManagerModal: AddManagerModalComponent;
    @ViewChild('editCustomerModal', {static: true}) editCustomerModal: EditCustomerModalComponent;

    @ViewChild('signaturePadComponent') signaturePadComponent: SignaturePadComponent;

    @ViewChild('dataTable', { static: true }) dataTable: Table;
    @ViewChild('paginator', { static: true }) paginator: Paginator;

    editCustomerOptions: any

    isValidSignature = false;

    protected readonly destroy$ = new Subject<void>(); // Used to signal unsubscription

    get selectedCustomer(): CustomerDto {
        return this._fieldAgentService.selectedCustomer;
    }

    get selectedCustomerVba(): VirtualBankAccountDto {
        return this._fieldAgentService.vba;
    }

    get total(): number {
        return Number(this._fieldAgentOrderService.orderTotalAmountValue);
    }

    get supplier(): string | undefined {
        return this._fieldAgentOrderService.selectedTargetValue || undefined;
    }

    notificationEmail = '';

    signature: string | undefined = undefined;

    constructor(
        injector: Injector,
        private _fieldAgentService: FieldAgentService,
        private _fieldAgentOrderService: FieldAgentOrderService,
        private _orderScheduleServiceProxy: OrderScheduleServiceProxy,
        private _router: Router
    ) {
        super(injector);

        this.spinnerService = injector.get(NgxSpinnerService);
    }

    ngOnInit() {
        this.spinnerService.show();

        this._fieldAgentService.vba$
            .pipe(
                takeUntil(this.destroy$),
                finalize(() => this.spinnerService.hide())
            )
            .subscribe(vba => {
                if (vba) {
                    this._fieldAgentService.getCustomerById$(vba.userId)
                        .pipe(
                            take(1),
                            finalize(() => this.spinnerService.hide())
                        )
                        .subscribe();
                }
            });

        this.getCommunicationEmail();

        this.editCustomerOptions = {
            requiredLegalName: true,
            requiredContactName: true,
            requiredAddress: true,
            requiredAddressHouseNumber: true,
            requiredAddressApartment: true,
            requiredAddressPostcode: true,
            requiredAddressCode: true,
            requiredAddressCity: true,
            requiredAddressCountry: true,
            requiredDeliveryContactName: false,
            requiredDeliveryAddress: false,
            requiredDeliveryAddressHouseNumber: false,
            requiredDeliveryAddressApartment: false,
            requiredDeliveryAddressPostcode: false,
            requiredDeliveryAddressCode: false,
            requiredDeliveryAddressCity: false,
            requiredDeliveryAddressCountry: false,
            requiredInvoiceContactName: false,
            requiredInvoiceAddress: false,
            requiredInvoiceAddressHouseNumber: false,
            requiredInvoiceAddressApartment: false,
            requiredInvoiceAddressPostcode: false,
            requiredInvoiceAddressCode: false,
            requiredInvoiceAddressCity: false,
            requiredInvoiceAddressCountry: false
        };
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    editCustomer(selectedCustomer: CustomerDto, mode: EditCustomerMode): void {
        if (this.signature) {
            abp.message.confirm(
                'When you proceed you will need to ask for a signature again. The signature is valid for the given context, not for a changed one.'
                , 'Signature lost'
                , (result: boolean) => {
                    if (result) {
                        this.signaturePadComponent.clearCanvas();
                        this._editCustomer(selectedCustomer, mode);
                    }
                }
                , {
                    showCloseButton: true,
                    customClass: {
                        container: 'modal-like'
                    }
                });
        } else {
            this._editCustomer(selectedCustomer, mode);
        }
    }

    private _editCustomer(selectedCustomer: CustomerDto, mode: EditCustomerMode): void {
        this._fieldAgentService.getCustomerForEdit$(selectedCustomer.id)
            .pipe(
                finalize(() => this.primengTableHelper.hideLoadingIndicator())
            )
            .subscribe((result: VirtualBankAccountDto) => {
                const input = {
                    id: result.id, // Assign the correct value if required
                    userId: selectedCustomer.id,
                    legalName: result.legalName,
                    contactEmailAddress: result.contactEmailAddress,
                    contactName: result.contactName,
                    address: result.address,
                    addressHouseNumber: result.addressHouseNumber,
                    addressApartment: result.addressApartment,
                    addressPostcode: result.addressPostcode,
                    addressCode: result.addressCode,
                    addressCity: result.addressCity,
                    addressCountry: result.addressCountry,
                    addressComment: result.addressComment,
                    invoiceContactName: result.invoiceContactName,
                    invoiceLegalName: result.legalName, // Assuming legalName applies to invoices
                    invoiceAddress: result.invoiceAddress,
                    invoiceAddressHouseNumber: result.invoiceAddressHouseNumber,
                    invoiceAddressApartment: result.invoiceAddressApartment,
                    invoiceAddressPostcode: result.invoiceAddressPostcode,
                    invoiceAddressCode: result.invoiceAddressCode,
                    invoiceAddressCity: result.invoiceAddressCity,
                    invoiceAddressCountry: result.invoiceAddressCountry,
                    invoiceAddressComment: '', // Assign a default value or map from result
                    deliveryContactName: result.deliveryContactName,
                    deliveryLegalName: result.legalName, // Assuming legalName applies to deliveries
                    deliveryAddress: result.deliveryAddress,
                    deliveryAddressHouseNumber: result.deliveryAddressHouseNumber,
                    deliveryAddressApartment: result.deliveryAddressApartment,
                    deliveryAddressPostcode: result.deliveryAddressPostcode,
                    deliveryAddressCode: result.deliveryAddressCode,
                    deliveryAddressCity: result.deliveryAddressCity,
                    deliveryAddressCountry: result.deliveryAddressCountry,
                    deliveryAddressComment: '', // Assign a default value or map from result
                    externalUserReference: result.externalUserReference,
                    phoneAvailability: result.phoneAvailability,
                    phoneNumber: result.phoneNumber,
                    phoneNumberSecondary: result.phoneNumberSecondary,
                    registrationNo: result.registrationNo,
                    personalRegistrationNo: result.personalRegistrationNo,
                    accountNumber: result.accountNumber,
                    accountBankName: result.accountBankName,
                    accountSwift: result.accountSwift,
                    accountBic: result.accountBic,
                    paymentTerms: result.paymentTerms,
                    accountRsin: result.accountRsin,
                    invoiceEAddress: result.invoiceEAddress,
                    invoiceRememberEInvoiceAddress: result.invoiceRememberEInvoiceAddress
                } as ICreateOrUpdateVirtualBankDto;

                this.editCustomerModal.showWithMode(input, mode);
            });
    }


    showChangeNotificationEmailModal() {
        if (this.signature) {
            abp.message.confirm(
                'When you proceed you will need to ask for a signature again. The signature is valid for the given context, not for a changed one.'
                , 'Signature lost'
                , (result: boolean) => {
                    if (result) {
                        this.signaturePadComponent.clearCanvas();
                        this.changeNotificationEmailModal.show(this.notificationEmail ?? '');
                    }
                }
                , {
                    showCloseButton: true,
                    customClass: {
                        container: 'modal-like'
                    }
                });
        } else {
            this.changeNotificationEmailModal.show(this.notificationEmail ?? '');
        }
    }

    private getCommunicationEmail() {
        this._orderScheduleServiceProxy.getCommunicationEmail(
                this._fieldAgentOrderService.orderScheduleIdValue
            )
            .subscribe((result: GetCommunicationEmailOutputDto) => {
                this.notificationEmail = result.email;
            });
    }

    onEditNotificationEmail(email: string) {
        this.notificationEmail = email;
    }

    onEditCustomer($event: any) {
        this._updateCustomer();
    }

    private _updateCustomer() {
        this._fieldAgentService.getVirtualBankAccountByUser$(abp.session.userId)
            .pipe(
                take(1)
            )
            .subscribe();
    }

    onSubmitAsDraft() {
        const input = {
            base64Sign: this.signature || '',
            executionDate: moment(),
            orderScheduleId: this._fieldAgentOrderService.orderScheduleIdValue,
            delayExecution: 7,
            notificationEmail: this.notificationEmail
        } as IConfirmWebshopOrderInputDto;
        this._confirmAndSignOrder(input);
    }

    onSubmit() {
        const input = {
            base64Sign: this.signature || '',
            executionDate: moment(),
            orderScheduleId: this._fieldAgentOrderService.orderScheduleIdValue,
            delayExecution: 0,
            notificationEmail: this.notificationEmail
        } as IConfirmWebshopOrderInputDto;
        this._confirmAndSignOrder(input);
    }

    private _confirmAndSignOrder(input: IConfirmWebshopOrderInputDto) {
        this._fieldAgentOrderService.confirmAndSignOrder$(input)
            .pipe(
                takeUntil(this.destroy$)
            )
            .subscribe((result: WebshopSendBasketOutput) => {
                this._fieldAgentOrderService.flush();
                this._router.navigate(['/app/webshop/field-agent/order-submitted']);
            });
    }

    get isSignatureValid() :boolean {
        return !this.isValidSignature && !_.isEmpty(this.signature);
    }

    updateSignature(sig: string) {
        this.signature = sig;
    }

    validateSignature(isValidSignature: boolean) {
        this.isValidSignature = isValidSignature;
    }

    clearSignature($event) {
        this.isValidSignature = false;
        this.signature = undefined;
    }
}
